import React from "react";
import Button from "../buttons/Button";
import ModalWrapper from "./ModalWrapper";
import { sanitizeBasicHtml } from "../../../utils/dom";
import { InformationCircle, X } from "../../icons/svg/outline";
import AnnouncementIcon from "../../common/AnnouncementIcon";

const CARD_CLASS =
  "relative w-full max-h-[90vh] overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-black/5";

/**
 * Message dialog with a title, body, and OK action.
 *
 * @param {Object} props Component props.
 * @param {boolean} props.isOpen Whether the modal is open.
 * @param {string} props.title Title text or basic HTML when ``html`` is true.
 * @param {string|React.ReactNode} props.message Body text, HTML, or a React node.
 * @param {Function} props.onClose Called when OK or the close control is used.
 * @param {boolean} [props.disableOK=false] Disable the OK button.
 * @param {boolean} [props.html=false] Render title and message as sanitized basic HTML.
 * @param {boolean} [props.inline=false] Render the card in-place (no overlay) for previews.
 * @param {string} [props.icon] Announcement icon key; omit for the generic info icon.
 * @returns {JSX.Element|null} The modal, or null when closed.
 */
const MessageModal = ({
  isOpen,
  title,
  message,
  onClose,
  disableOK = false,
  html = false,
  inline = false,
  icon,
}) => {
  if (!isOpen) return null;

  const htmlClasses =
    "[&_a]:font-medium [&_a]:text-primary [&_a]:underline [&_a]:underline-offset-2 [&_a]:hover:text-primary-300 [&_p]:mb-2 last:[&_p]:mb-0 [&_ul]:mb-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:mb-2 [&_ol]:list-decimal [&_ol]:pl-5";

  const card = (
    <>
      <div className="flex items-center gap-3 px-6 pb-2 pt-6">
        {icon ? (
          <span
            className={`flex h-9 w-9 shrink-0 items-center justify-center ${
              icon === "rs"
                ? ""
                : "rounded-full bg-primary/10 text-primary"
            }`}
          >
            <AnnouncementIcon icon={icon} />
          </span>
        ) : (
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
            <InformationCircle className="h-5 w-5" />
          </span>
        )}
        {html ? (
          <h2
            className={`min-w-0 flex-1 text-left text-lg font-semibold leading-9 tracking-tight text-gray-950 ${htmlClasses}`}
            dangerouslySetInnerHTML={{ __html: sanitizeBasicHtml(title) }}
          />
        ) : (
          <h2 className="min-w-0 flex-1 text-left text-lg font-semibold leading-9 tracking-tight text-gray-950">
            {title}
          </h2>
        )}
        <button
          type="button"
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-800"
          onClick={onClose}
          tabIndex={inline ? -1 : undefined}
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      <div className="px-6 pb-0 pl-[4.5rem] text-left text-[15px] leading-relaxed text-gray-600">
        {html ? (
          <div
            className={htmlClasses}
            dangerouslySetInnerHTML={{ __html: sanitizeBasicHtml(message) }}
          />
        ) : typeof message === "string" ? (
          <p className="whitespace-pre-line">{message}</p>
        ) : (
          message
        )}
      </div>

      <div className="flex justify-end border-t border-gray-100 bg-gray-50/80 px-6 py-4">
        <Button
          onClick={onClose}
          text="OK"
          variant={disableOK ? "disabled" : "primary"}
          className="min-w-[5.5rem] px-5 py-2 text-sm font-semibold"
        />
      </div>
    </>
  );

  if (inline) {
    return <div className={CARD_CLASS}>{card}</div>;
  }

  return (
    <ModalWrapper
      isOpen={isOpen}
      onClose={onClose}
      maxWidth={480}
      closeButton={false}
      className={CARD_CLASS}
    >
      {card}
    </ModalWrapper>
  );
};

export default MessageModal;
